import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';

import { BusinessDocument } from '../../core/services/business-document.service';

@Component({
  selector: 'app-verification-drawer',
  standalone: false,
  templateUrl: './verification-drawer.html',
  styleUrl: './verification-drawer.scss',
})
export class VerificationDrawer implements OnChanges {
  @Input() open = false;
  @Input() embedded = false;
  @Input() businessDocumentId = '';
  @Input() requests: Array<Record<string, unknown>> = [];
  @Output() closed = new EventEmitter<void>();
  @Output() changed = new EventEmitter<void>();

  notes: Array<Record<string, unknown>> = [];
  noteBody = '';
  selectedRequestId = '';
  loading = false;
  saving = false;
  pendingRequestId = '';
  decisionNotes: Record<string, string> = {};

  constructor(
    private readonly documents: BusinessDocument,
    private readonly snackBar: MatSnackBar,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['open'] || changes['businessDocumentId']) && this.open) {
      void this.loadNotes();
    }
  }

  async loadNotes(): Promise<void> {
    if (!this.businessDocumentId) return;
    this.loading = true;
    try {
      this.notes = await firstValueFrom(this.documents.notes(this.businessDocumentId));
    } catch {
      this.snackBar.open('Verification notes could not be loaded.', 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar'],
      });
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  async addNote(): Promise<void> {
    const body = this.noteBody.trim();
    if (!body || this.saving) return;
    this.saving = true;
    try {
      await firstValueFrom(
        this.documents.addNote(
          this.businessDocumentId,
          body,
          this.selectedRequestId || null,
          null,
        ),
      );
      this.noteBody = '';
      await this.loadNotes();
      this.changed.emit();
    } catch {
      this.snackBar.open('The note could not be added.', 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar'],
      });
    } finally {
      this.saving = false;
      this.cdr.detectChanges();
    }
  }

  async decide(
    request: Record<string, unknown>,
    decision: 'approved' | 'changes_requested',
  ): Promise<void> {
    const id = this.text(request['id']);
    const note = (this.decisionNotes[id] ?? '').trim();
    if (!id || this.pendingRequestId || (decision === 'changes_requested' && !note)) return;
    this.pendingRequestId = id;
    try {
      await firstValueFrom(this.documents.decideVerification(id, decision, note));
      this.snackBar.open(
        decision === 'approved' ? 'Verification approved.' : 'Changes requested.',
        'Close',
        { duration: 3000, panelClass: ['success-snackbar'] },
      );
      this.changed.emit();
    } catch (error: any) {
      this.snackBar.open(
        error?.error?.message ?? 'The verification response could not be saved.',
        'Close',
        { duration: 5000, panelClass: ['error-snackbar'] },
      );
    } finally {
      this.pendingRequestId = '';
      this.cdr.detectChanges();
    }
  }

  close(): void {
    this.closed.emit();
  }

  text(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }
}
